import { useState, useRef, useEffect, useCallback } from 'react';
import { bffClient, isDebugMode } from '@sluitstraat/core';
import {
  mapToCalculationRequest,
  mapFromCalculationResponse,
  cleanUnselectedCoveragePremiums,
  applyWoonhuisDiscountInPlace,
} from './calc';

// Owns the Woonhuis premium-calculation machinery, moved verbatim from WoonhuisScreen:
// the debounced auto-calc effect (input signature over the premium-relevant fields, 800 ms
// debounce, monument gate, hasOptionalSelectValues check) and callCalculation (address gate,
// BFF call, response merge + hidden-discount bake + originalPremiumsRef update). The screen
// keeps the returned state/refs because loadSchema, handleNext, handleManualCalculate and
// handleRiskAddressSameChange also drive them. Mirrors inboedel/useCoverageCalculation.ts.

interface CoverageCalculationArgs {
  screenId: string;
  data: any;
  setData: (updater: any) => void;
  saveWorkflowData: (stepId: string, displayData: any) => void;
  t: (key: string, opts?: any) => string;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  loading: boolean;
  isMonumentProperty: boolean;
  isAddressLocked: boolean;
  isBuildingDetailsLocked: boolean;
  ppEkst: number;
  woonhuisDiscountRef: React.MutableRefObject<{ enabled: boolean; percentage: number; excludeCoverages: string[]; applyToData: boolean }>;
  undiscountedDataRef: React.MutableRefObject<any>;
}

export function useCoverageCalculation({
  screenId,
  data,
  setData,
  saveWorkflowData,
  t,
  setValidationErrors,
  loading,
  isMonumentProperty,
  isAddressLocked,
  isBuildingDetailsLocked,
  ppEkst,
  woonhuisDiscountRef,
  undiscountedDataRef,
}: CoverageCalculationArgs) {
  const [isCalculating, setIsCalculating] = useState(false);
  const [lastCalculationInput, setLastCalculationInput] = useState<any>(null);
  const [lastCalculationOutput, setLastCalculationOutput] = useState<any>(null);
  const [shouldAutoCalculate, setShouldAutoCalculate] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [calculationSuccess, setCalculationSuccess] = useState(false);

  // Store original calculation premiums for dynamic total calculation
  const originalPremiumsRef = useRef<{
    PP_TTOT: number;
    OP_TTOT: number;  // Base Woonhuis premium (from BFF)
    GP_TTOT: number;
  } | null>(null);

  // Track if initial load is complete to enable auto-calculation
  const initialLoadComplete = useRef(false);
  const autoCalculationTimer = useRef<number | null>(null);
  const isUpdatingFromCalculation = useRef(false);
  const lastCalculatedData = useRef<string>('');

  // Call BFF calculation endpoint
  const callCalculation = useCallback(async (formData: any, isManual: boolean = false) => {
    // Check if address is validated before calculating
    // Skip when address is locked from a previous screen - it was already validated upstream
    const ba = formData?.Contractdocument?.PP?.BA;
    if (!isAddressLocked && (!ba?.BA_STRAAT || !ba?.BA_PLAATS)) {
      if (isManual) {
        setCalculationError(t('common.addressInvalid'));
      }
      setValidationErrors(prev => ({
        ...prev,
        'Contractdocument.PP.BA.BA_STRAAT': t('screens.woonhuis.validation.addressNotValidated')
      }));
      return;
    }

    try {
      setIsCalculating(true);
      setCalculationError(null);
      setCalculationSuccess(false);

      // Map form data to calculation request using dedicated mapper
      const requestBody = mapToCalculationRequest(formData, ppEkst, { baseUrl: window.location.origin + '/', now: new Date() });


      // Store input (the wrapped request)
      setLastCalculationInput(requestBody);

      const response = await bffClient.request<any>(
        'POST',
        '/api/v1/productservice/calculate',
        requestBody
      );

      // Store raw BFF response for debug panel (always undiscounted)
      setLastCalculationOutput(JSON.parse(JSON.stringify(response)));

      // Map response to form data updates using dedicated mapper
      if (response) {
        const updatedFormData = mapFromCalculationResponse(response, formData);
        // Keep undiscounted copy for workflow persistence (when applyToData is false)
        const undiscountedFormData = JSON.parse(JSON.stringify(updatedFormData));

        // Apply hidden woonhuis discount for display
        const discountCfg = woonhuisDiscountRef.current;

        // Store undiscounted ref only when discount is active and applyToData is false
        if (discountCfg.enabled && !discountCfg.applyToData) {
          undiscountedDataRef.current = undiscountedFormData;
        } else {
          undiscountedDataRef.current = null;
        }
        applyWoonhuisDiscountInPlace(updatedFormData, discountCfg);

        // Store discounted premiums for dynamic total updates (UI display)
        const pp = updatedFormData?.Contractdocument?.PP;
        originalPremiumsRef.current = {
          PP_TTOT: Number(pp?.PP_TTOT) || 0,
          OP_TTOT: Number(pp?.OP?.OP_TTOT) || 0,
          GP_TTOT: Number(pp?.GP?.GP_TTOT) || 0
        };

        // Set flag to prevent auto-calculation loop
        isUpdatingFromCalculation.current = true;

        // Display state: always discounted
        setData(updatedFormData);

        // Save to workflow (saveWorkflowData handles undiscounted replacement when needed)
        const cleanedData = cleanUnselectedCoveragePremiums(updatedFormData);
        saveWorkflowData(screenId, cleanedData);
      }

      setCalculationSuccess(true);
      setIsCalculating(false);
    } catch (error) {

      // Parse error message
      let errorMessage = 'Calculation failed. Please check your input and try again.';

      if (error instanceof Error) {
        const errorStr = error.message;
        if (errorStr.includes('400')) {
          errorMessage = 'Invalid input data. Please check all required fields are filled correctly.';
          // DEV: 400 errors can also indicate BFF permissions/rights issues - verify API access rights
          if (isDebugMode()) {
            console.warn('[DEV] 400 error may indicate BFF permissions issue. Verify API access rights for R515/Woonhuis_2024.');
          }
        } else if (errorStr.includes('500')) {
          errorMessage = 'Server error during calculation. Please try again later.';
        } else if (errorStr.includes('405')) {
          errorMessage = 'Calculation service temporarily unavailable. Please try again.';
        } else {
          errorMessage = `Calculation failed: ${errorStr}`;
        }
      }

      setCalculationError(errorMessage);
      setLastCalculationOutput({ error: errorMessage });
      setIsCalculating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- takes its inputs as arguments/refs; one stable reference for the debounced auto-calc effect
  }, []);

  // Helper: Check if required fields are filled
  const getRequiredFields = useCallback(() => {
    const ba = data?.Contractdocument?.PP?.BA;
    const op = data?.Contractdocument?.PP?.OP;

    // Address fields (skip if locked)
    const addressFields = !isAddressLocked ? {
      'BA_PCODE': ba?.BA_PCODE,
      'BA_HUISNR': ba?.BA_HUISNR,
      'BA_STRAAT': ba?.BA_STRAAT,
      'BA_PLAATS': ba?.BA_PLAATS
    } : {};

    // Building detail fields (skip if locked)
    const buildingFields = !isBuildingDetailsLocked ? {
      'BA_BOUWJR': ba?.BA_BOUWJR,
      'BA_TYPEWON': ba?.BA_TYPEWON,
      'BA_AARDBW': ba?.BA_AARDBW,
      'BA_AARDDAK': ba?.BA_AARDDAK,
      'BA_WONING': ba?.BA_WONING,
      'BA_HERBVST': ba?.BA_HERBVST
    } : {};

    // Always required fields - Woonhuisdekking values must both be set before calculation
    const alwaysRequired: Record<string, any> = {
      'OP_VERZSOM': op?.OP_VERZSOM
    };

    // Woonhuis is first in workflow - always require OP_GSS here
    alwaysRequired['OP_GSS'] = op?.OP_GSS;

    return { ...addressFields, ...buildingFields, ...alwaysRequired };
  }, [data, isAddressLocked, isBuildingDetailsLocked]);

  // Auto-calculation: Trigger calculation when all required fields are filled
  useEffect(() => {
    // Don't auto-calculate until initial load is complete
    // Block auto-calculation if property is a monument (cannot be insured)
    if (!initialLoadComplete.current || loading || isCalculating || isMonumentProperty) {
      return;
    }

    // Skip if we're updating from a calculation result
    if (isUpdatingFromCalculation.current) {
      isUpdatingFromCalculation.current = false;
      return;
    }

    // Clear any existing timer
    if (autoCalculationTimer.current) {
      clearTimeout(autoCalculationTimer.current);
    }

    // Create a signature of input fields (not calculated output fields)
    const ba = data?.Contractdocument?.PP?.BA;
    const op = data?.Contractdocument?.PP?.OP;
    const vp = data?.Contractdocument?.PP?.VP;

    const inputSignature = JSON.stringify({
      BA_PCODE: ba?.BA_PCODE,
      BA_HUISNR: ba?.BA_HUISNR,
      BA_STRAAT: ba?.BA_STRAAT,
      BA_PLAATS: ba?.BA_PLAATS,
      BA_BOUWJR: ba?.BA_BOUWJR,
      BA_TYPEWON: ba?.BA_TYPEWON,
      BA_AARDBW: ba?.BA_AARDBW,
      BA_AARDDAK: ba?.BA_AARDDAK,
      BA_WONING: ba?.BA_WONING,
      BA_HERBVST: ba?.BA_HERBVST,
      BA_TOEVOEG: ba?.BA_TOEVOEG,
      BA_BDRFBEL: ba?.BA_BDRFBEL,
      BA_OPPWON: ba?.BA_OPPWON,
      BA_EIGENWO: ba?.BA_EIGENWO,
      BA_PERMBEW: ba?.BA_PERMBEW,
      RisicoAdresGelijk: data?.Supportsection?.Divers?.RisicoAdresGelijk,
      OP_GSS: op?.OP_GSS,
      OP_VERZSOM: op?.OP_VERZSOM,
      OP_ERB: op?.OP_ERB,
      OP_STORM: op?.OP_STORM,
      VP_GEBDAT: vp?.VP_GEBDAT,
      Hoofddekking: data?.Supportsection?.Dekkingen?.Hoofddekking
      // NOTE: Glas coverage selection excluded from signature
      // to prevent recalculation when toggling - prices are pre-calculated
    });

    // Check if all required fields are filled using helper
    const requiredFields = getRequiredFields();
    const hasAllRequiredFields = Object.values(requiredFields).every(value => value != null && value !== '');

    // Also check that optional select fields have actual values (not empty) before recalculating
    // This prevents recalculation when select fields are changed but no actual value is set
    const hasOptionalSelectValues = (op?.OP_ERB != null && op?.OP_ERB !== '') &&
                                    (op?.OP_STORM != null && op?.OP_STORM !== '');

    // Handle initial load auto-calculation trigger
    if (shouldAutoCalculate) {
      setShouldAutoCalculate(false);
      // Skip recalculation if data hasn't changed (e.g., navigating back to page)
      if (inputSignature === lastCalculatedData.current) {
        return;
      }
      if (hasAllRequiredFields && hasOptionalSelectValues) {
        lastCalculatedData.current = inputSignature;
        callCalculation(data, false);
      }
      return;
    }

    // Check if input data actually changed (for subsequent changes)
    if (inputSignature === lastCalculatedData.current) {
      return;
    }

    // Debounce auto-calculation by 800ms to avoid excessive API calls
    if (hasAllRequiredFields && hasOptionalSelectValues) {
      autoCalculationTimer.current = setTimeout(() => {
        lastCalculatedData.current = inputSignature;
        callCalculation(data, false);
      }, 800);
    }

    // Cleanup timer on unmount
    return () => {
      if (autoCalculationTimer.current) {
        clearTimeout(autoCalculationTimer.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, isCalculating, getRequiredFields, isAddressLocked, isBuildingDetailsLocked, shouldAutoCalculate, isMonumentProperty]);

  return {
    isCalculating,
    setIsCalculating,
    calculationError,
    setCalculationError,
    calculationSuccess,
    setCalculationSuccess,
    lastCalculationInput,
    setLastCalculationInput,
    lastCalculationOutput,
    setLastCalculationOutput,
    shouldAutoCalculate,
    setShouldAutoCalculate,
    initialLoadComplete,
    isUpdatingFromCalculation,
    lastCalculatedData,
    originalPremiumsRef,
    callCalculation,
  };
}
